import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import MigrationBreadcrumb from "./MigrationBreadcrumb";
import DownloadWithPopup from "./DownloadPopup";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { useNavigate } from "react-router-dom";
import norecords from "../../assests/norecords.gif";
import isEqual from "lodash/isEqual";
import { useLocation } from "react-router-dom";
import CustomTooltip from "../Buttons/CustomTooltip";
import GitHubUploadPanel from "./GitHubUploadModal";

// Table cell styling
const cellStyle = {
  textAlign: "left",
  verticalAlign: "middle",
};

// Defined at module scope (not inside MigrationTable) on purpose: this is
// used many times per row (job name, description, source/target db, per
// file cells, ...). If it were defined inside the component's render body,
// every re-render would create a brand new function reference for it, and
// React treats that as a different component type - forcing it to unmount
// and remount every single instance (losing DOM, tooltips, etc.) on every
// render instead of just patching them in place. With a big/long-running job,
// the table re-renders every ~5s (websocket updates) plus on every checkbox
// click, so this was compounding into very noticeable input lag.
const TruncatedText = ({
  text,
  maxLength = 16,
  jobId,
  newlyCreatedJob,
  isFile = false,
}) => {
  if (!text) return "N/A";
  const isNewJob = jobId && newlyCreatedJob === jobId;
  const isTruncated = text.length > maxLength;
  const truncated = isTruncated ? `${text.substring(0, maxLength)}...` : text;
  if (isNewJob || isTruncated) {
    return (
      <CustomTooltip
        content={text}
        placement="right"
        forceShow={isNewJob}
        jobId={jobId}
      >
        <span>{truncated}</span>
      </CustomTooltip>
    );
  }
  return <span>{truncated}</span>;
};

function MigrationTable({
  isDeletedView,
  logsetShow,
  consetShow,
  setSelectedJobId,
  setselectedJobname,
  setselectedFilename,
  setSelectedFileId,
  setSelectedFileStatus,
  setShowEdit,
  onEditJob,
  refreshKey,
  setShow,
  setCurrentStep,
  executingJobs,
  setExecutingJobs,
  setRefreshKey,
  IsDraftView,
  newlyCreatedJob,
  pendingJobPreview,
  isGithubConfigured, // Our new prop
}) {
  // State for table data and UI
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL
  const activeTab = location.pathname.includes("draft_jobs")
    ? "draftjobs"
    : "migrationjobs";
  const [expandedRows, setExpandedRows] = useState({});
  const [checkedRows, setCheckedRows] = useState({});
  const [indeterminateRows, setIndeterminateRows] = useState({});
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(false);
  const [isDeletingpermanently, setIsDeletingpermanently] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [visibleIds, setVisibleIds] = useState(new Set());
  const [headerIndeterminate, setHeaderIndeterminate] = useState(false);
  const [activeJobs, setActiveJobs] = useState([]);
  const [inactiveJobs, setInactiveJobs] = useState([]);
  const [draftJobs, setDraftJobs] = useState([]); // New state for draft jobs
  // Jobs whose execution was just triggered locally, shown immediately in the
  // table while we wait for the websocket to confirm them from the server.
  const [optimisticJobs, setOptimisticJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [githubPanelState, setGithubPanelState] = useState({
    isOpen: false,
    jobId: null,
    fileId: null,
    jobName: "",
    fileName: "",
  });

  const socketRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const maxRetryDelay = 30000;

  // Modal state management
  const [modalState, setModalState] = useState({
    isOpen: false,
    action: null,
    jobId: null,
    fileId: null,
    isLoading: false,
  });

  const headerCheckboxRef = useRef(null);
  const prevExecutingJobsRef = useRef(executingJobs);
  const prevDataRef = useRef([]);
  const isInitialMount = useRef(true);

  const hasInProgressItems = () => {
    for (const jobId of selectedJobIds) {
      const job = data.find((j) => j.job_id === jobId);
      if (job && job.status === "In Progress") {
        return true;
      }
    }

    for (const fileId of selectedFileIds) {
      const jobWithFile = data.find((j) =>
        j.files?.some((f) => f.file_id === fileId)
      );
      if (jobWithFile) {
        const file = jobWithFile.files.find((f) => f.file_id === fileId);
        if (file && file.status === "In Progress") {
          return true;
        }
        const hasInProgressFiles = jobWithFile.files?.some(
          (f) => f.status === "In Progress"
        );
        if (hasInProgressFiles) {
          return true;
        }
      }
    }
    return false;
  };

  // Overlay any locally-triggered (not yet server-confirmed) jobs on top of
  // the real active jobs list, so newly executed jobs appear instantly.
  const mergedActiveJobs = useMemo(() => {
    if (optimisticJobs.length === 0) return activeJobs;
    const stillPending = optimisticJobs.filter(
      (oj) => !activeJobs.some((rj) => rj.job_id === oj.job_id)
    );
    return stillPending.length > 0
      ? [...stillPending, ...activeJobs]
      : activeJobs;
  }, [activeJobs, optimisticJobs]);

  useEffect(() => {
    if (!pendingJobPreview) return;
    setOptimisticJobs((prev) => {
      if (pendingJobPreview.__remove) {
        return prev.filter((job) => job.job_id !== pendingJobPreview.job_id);
      }
      const withoutExisting = prev.filter(
        (job) => job.job_id !== pendingJobPreview.job_id
      );
      return [pendingJobPreview, ...withoutExisting];
    });
  }, [pendingJobPreview]);

  useEffect(() => {
    const currentJobs = isDeletedView
      ? inactiveJobs
      : activeTab === "draftjobs"
      ? draftJobs
      : mergedActiveJobs;
    if (!isEqual(prevDataRef.current, currentJobs)) {
      setData(currentJobs);
      setFilteredData(currentJobs);
      prevDataRef.current = currentJobs;
    }
  }, [isDeletedView, mergedActiveJobs, inactiveJobs, activeTab, draftJobs]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredData(data);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = data.filter((row) => {
      const jobMatch = [
        row.job_name?.toLowerCase(),
        row.description?.toLowerCase(),
        row.source_db?.toLowerCase(),
        row.target_db?.toLowerCase(),
        row.status?.toLowerCase(),
        row.created_at?.toLowerCase(),
      ].some((field) => field?.includes(query));
      const fileMatch = row.files?.some((file) =>
        [
          file.file_name?.toLowerCase(),
          file.description?.toLowerCase(),
          file.status?.toLowerCase(),
          file.created_at?.toLowerCase(),
        ].some((field) => field?.includes(query))
      );
      return jobMatch || fileMatch;
    });
    setFilteredData(filtered);
  }, [data, searchQuery]);

  useEffect(() => {
    setCheckedRows({});
    setSelectedIds([]);
    setSelectedJobIds([]);
    setIndeterminateRows({});
    setSelectedFileIds([]);
    setSelectAll(false);
  }, [isDeletedView, activeTab]);

  const handleGitHubUploadClick = (
    jobId,
    jobName,
    fileId = null,
    fileName = null,
    source_db = null,
    targetDb = null
  ) => {
    setGithubPanelState({
      isOpen: true,
      jobId,
      fileId,
      jobName,
      fileName,
      source_db,
      targetDb,
    });
  };

  const handleGitHubUploadSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const closeGitHubModal = () => {
    setGithubPanelState({
      isOpen: false,
      jobId: null,
      fileId: null,
      jobName: "",
      fileName: "",
    });
  };

  const arraysEqual = (a, b) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const handleCancelSelection = () => {
    setCheckedRows({});
    setSelectedJobIds([]);
    setSelectedFileIds([]);
    setSelectAll(false);
    setHeaderIndeterminate(false);
    setIsDeleteEnabled(false);
    setIsEditEnabled(false);
  };

  const handleRestore = async (jobId, fileId = null) => {
    const apiBase = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    try {
      setModalState((prev) => ({ ...prev, isLoading: true }));
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) throw new Error("Authentication token missing");
      let url = `${apiBase}/restore_job/${jobId}`;
      if (fileId) {
        url += `?file_id=${fileId}`;
      }
      await axios.put(url, null, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success(
        "The job has been successfully restored and moved to the job list.",
        {
          style: getToastStyle(),
          progressStyle: { background: "#50b848" },
        }
      );
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Restore failed:", error);
      toast.error(`Failed to restore ${fileId ? "file" : "job"}`, {
        style: ErrorToastStyle(),
      });
    } finally {
      setModalState({
        isOpen: false,
        action: null,
        jobId: null,
        fileId: null,
        isLoading: false,
      });
    }
  };

  const handleBulkRestore = async () => {
    const apiBaseTable = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    if (selectedJobIds.length === 0 && selectedFileIds.length === 0) return;
    try {
      setModalState((prev) => ({ ...prev, isLoading: true }));
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) throw new Error("Authentication token missing");
      if (selectedFileIds.length === 0) {
        await Promise.all(
          selectedJobIds.map((jobId) =>
            axios.put(`${apiBaseTable}/restore_job/${jobId}`, null, {
              headers: { Authorization: `Bearer ${authToken}` },
            })
          )
        );
      } else {
        const filesByJob = {};
        selectedFileIds.forEach((fileId) => {
          const parentJob = data.find((job) =>
            job.files?.some((file) => file.file_id === fileId)
          );
          if (parentJob) {
            if (!filesByJob[parentJob.job_id]) {
              filesByJob[parentJob.job_id] = [];
            }
            filesByJob[parentJob.job_id].push(fileId);
          }
        });
        const jobsToRestore = selectedJobIds.filter(
          (jobId) => !filesByJob[jobId]
        );
        await Promise.all(
          jobsToRestore.map((jobId) =>
            axios.put(`${apiBaseTable}/restore_job/${jobId}`, null, {
              headers: { Authorization: `Bearer ${authToken}` },
            })
          )
        );
        await Promise.all(
          Object.entries(filesByJob).flatMap(([jobId, fileIds]) =>
            fileIds.map((fileId) =>
              axios.put(
                `${apiBaseTable}/restore_job/${jobId}?file_id=${fileId}`,
                null,
                {
                  headers: { Authorization: `Bearer ${authToken}` },
                }
              )
            )
          )
        );
      }
      setCheckedRows({});
      setSelectedJobIds([]);
      setSelectedFileIds([]);
      setRefreshKey((prev) => prev + 1);
      return true;
    } catch (error) {
      console.error("Restore failed:", error);
      throw error;
    } finally {
      setModalState({
        isOpen: false,
        action: null,
        jobId: null,
        fileId: null,
        isLoading: false,
      });
    }
  };

  const handleDelete = async () => {
    const apiBasehandle = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    if (!isDeleteEnabled) return;
    if (hasInProgressItems()) {
      toast.dismiss();
      toast.error(
        "Items cannot be deleted while files are still in progress. Please wait for all files to complete.",
        {
          style: ErrorToastStyle(),
          autoClose: 5000,
        }
      );
      setModalState({ ...modalState, isOpen: false });
      return;
    }
    setIsDeleting(true);
    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) throw new Error("Authentication token missing.");
      const checkedItems = Object.entries(checkedRows)
        .filter(([_, isChecked]) => isChecked)
        .map(([key]) => key);
      const selectedJobKeys = checkedItems.filter((key) =>
        key.startsWith("job-")
      );
      const selectedFileKeys = checkedItems.filter((key) =>
        key.startsWith("file-")
      );
      const selectedJobIds = selectedJobKeys.map((key) =>
        Number(key.split("-")[1])
      );
      const selectedFileIds = selectedFileKeys.map((key) =>
        Number(key.split("-")[1])
      );
      const filesByJob = {};
      selectedFileIds.forEach((fileId) => {
        const parentJob = data.find((job) =>
          job.files?.some((file) => file.file_id === fileId)
        );
        if (parentJob) {
          if (!filesByJob[parentJob.job_id]) {
            filesByJob[parentJob.job_id] = [];
          }
          filesByJob[parentJob.job_id].push(fileId);
        }
      });
      const deleteRequests = [];
      selectedJobIds.forEach((jobId) => {
        const params = new URLSearchParams();
        params.append("job_ids", jobId);
        deleteRequests.push(
          axios.delete(`${apiBasehandle}/delete_files_or_job/`, {
            headers: { Authorization: `Bearer ${authToken}` },
            params,
          })
        );
      });
      Object.entries(filesByJob).forEach(([jobId, fileIds]) => {
        if (!selectedJobIds.includes(Number(jobId))) {
          const params = new URLSearchParams();
          params.append("job_ids", jobId);
          fileIds.forEach((id) => params.append("file_ids", id));
          deleteRequests.push(
            axios.delete(`${apiBasehandle}/delete_files_or_job/`, {
              headers: { Authorization: `Bearer ${authToken}` },
              params,
            })
          );
        }
      });
      if (deleteRequests.length > 0) {
        await Promise.all(deleteRequests);
        const updatedData = data
          .filter((job) => !selectedJobIds.includes(job.job_id))
          .map((job) => ({
            ...job,
            files: job.files?.filter(
              (file) =>
                !selectedFileIds.includes(file.file_id) &&
                !selectedJobIds.includes(job.job_id)
            ),
          }));
        setData(updatedData);
        setFilteredData(updatedData);
        setCheckedRows((prev) => {
          const newChecked = { ...prev };
          selectedJobIds.forEach((jobId) => delete newChecked[`job-${jobId}`]);
          selectedFileIds.forEach(
            (fileId) => delete newChecked[`file-${fileId}`]
          );
          return newChecked;
        });
        setSelectedIds([]);
        setSelectedJobIds([]);
        const jobText = selectedJobIds.length === 1 ? "job" : "jobs";
        const fileText = selectedFileIds.length === 1 ? "file" : "files";
        if (selectedJobIds.length > 0 && selectedFileIds.length > 0) {
          toast.success(`Selected items deleted successfully.`, {
            style: getToastStyle(),
          });
        } else if (selectedJobIds.length > 0) {
          toast.success(
            `${
              jobText.charAt(0).toUpperCase() + jobText.slice(1)
            } and all linked files have been deleted.`,
            {
              style: getToastStyle(),
            }
          );
        } else if (selectedFileIds.length > 0) {
          toast.success(
            `Selected ${fileText} have been removed from their jobs.`,
            {
              style: getToastStyle(),
            }
          );
        }
      }
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
      setModalState({ ...modalState, isOpen: false });
    }
  };

  const handleRunDraftJob = async (jobId) => {
    const apiBaseDraft = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) throw new Error("Authentication token missing");
      toast.success(
        "Draft job execution started! This job is now moving to code conversion table.",
        {
          style: getToastStyle(),
          autoClose: 3000,
        }
      );
      setExecutingJobs((prev) => [...prev, jobId]);
      await axios.get(`${apiBaseDraft}/execute?job_ids=${jobId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success("Job execution successfully completed.", {
        style: getToastStyle(),
        progressStyle: { background: "#50b848" },
      });
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to run draft job:", error);
      setExecutingJobs((prev) => prev.filter((id) => id !== jobId));
      toast.error("Failed to start job execution. Please try again.", {
        style: ErrorToastStyle(),
      });
    }
  };

  const openActionModal = (
    action,
    id = null,
    isFile = false,
    parentJobId = null
  ) => {
    setModalState({
      isOpen: true,
      action,
      jobId: isFile ? parentJobId : id,
      fileId: isFile ? id : null,
      isLoading: false,
    });
  };

  const handlePermanentDelete = async () => {
    const apiBasedelete = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    const itemsToDelete = modalState.itemsToDelete || {
      jobIds: selectedJobIds,
      fileIds: selectedFileIds,
    };
    const { jobIds, fileIds } = itemsToDelete;
    if (jobIds.length === 0 && fileIds.length === 0) return;
    if (hasInProgressItems()) {
      toast.dismiss();
      toast.error(
        "Items cannot be deleted while files are still in progress. Please wait for all files to complete.",
        {
          style: ErrorToastStyle(),
          autoClose: 5000,
        }
      );
      setModalState({ ...modalState, isOpen: false });
      return;
    }
    setIsDeletingpermanently(true);
    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) throw new Error("Authentication token missing");
      const filesByJob = {};
      fileIds.forEach((fileId) => {
        const parentJob = data.find((job) =>
          job.files?.some((file) => file.file_id === fileId)
        );
        if (parentJob) {
          if (!filesByJob[parentJob.job_id]) {
            filesByJob[parentJob.job_id] = [];
          }
          filesByJob[parentJob.job_id].push(fileId);
        }
      });
      const deleteRequests = [];
      jobIds.forEach((jobId) => {
        const params = new URLSearchParams();
        params.append("job_ids", jobId);
        deleteRequests.push(
          axios.delete(`${apiBasedelete}/permanently_delete_files_and_jobs`, {
            headers: { Authorization: `Bearer ${authToken}` },
            params,
          })
        );
      });
      Object.entries(filesByJob).forEach(([jobId, fileIds]) => {
        if (!jobIds.includes(Number(jobId))) {
          const params = new URLSearchParams();
          params.append("job_ids", jobId);
          fileIds.forEach((id) => params.append("file_ids", id));
          deleteRequests.push(
            axios.delete(`${apiBasedelete}/permanently_delete_files_and_jobs`, {
              headers: { Authorization: `Bearer ${authToken}` },
              params,
            })
          );
        }
      });
      if (deleteRequests.length > 0) {
        await Promise.all(deleteRequests);
        const updatedData = data
          .filter((job) => !jobIds.includes(job.job_id))
          .map((job) => ({
            ...job,
            files: job.files?.filter(
              (file) =>
                !fileIds.includes(file.file_id) && !jobIds.includes(job.job_id)
            ),
          }));
        setData(updatedData);
        setFilteredData(updatedData);
        if (!modalState.itemsToDelete) {
          setCheckedRows((prev) => {
            const newChecked = { ...prev };
            jobIds.forEach((jobId) => delete newChecked[`job-${jobId}`]);
            fileIds.forEach((fileId) => delete newChecked[`file-${fileId}`]);
            return newChecked;
          });
          setSelectedIds([]);
          setSelectedJobIds([]);
          setSelectedFileIds([]);
        }
        const jobText = jobIds.length === 1 ? "job" : "jobs";
        const fileText = fileIds.length === 1 ? "file" : "files";
        if (jobIds.length > 0 && fileIds.length > 0) {
          toast.success(
            `Selected ${jobText} and ${fileText} were permanently deleted successfully.`,
            {
              style: getToastStyle(),
            }
          );
        } else if (jobIds.length > 0) {
          toast.success(
            `${
              jobText.charAt(0).toUpperCase() + jobText.slice(1)
            } and all linked files have been permanently deleted.`,
            {
              style: getToastStyle(),
            }
          );
        } else if (fileIds.length > 0) {
          toast.success(
            `Selected ${fileText} have been permanently removed from their jobs.`,
            {
              style: getToastStyle(),
            }
          );
        }
      }
    } catch (error) {
      console.error("Permanent delete error:", error);
      toast.error("Failed to permanently delete items", {
        style: ErrorToastStyle(),
      });
    } finally {
      setIsDeletingpermanently(false);
      setModalState({ ...modalState, isOpen: false });
    }
  };

  const handleActionConfirm = () => {
    if (modalState.action === "delete") {
      handleDelete();
    } else if (modalState.action === "delete permanently") {
      handlePermanentDelete();
    } else if (modalState.jobId) {
      handleRestore(modalState.jobId, modalState.fileId);
    } else {
      handleBulkRestore();
      setModalState({ ...modalState, isOpen: false });
    }
  };

  const getToastStyle = () => ({
    background: "#d4f4d2",
    color: "#000",
    width: "fit-content",
    minWidth: "fit-content",
    textAlign: "left",
    top: "18px",
    borderRadius: "8px",
    fontSize: "14px",
    padding: "12px",
  });

  const ErrorToastStyle = () => ({
    background: "#FFEBEE",
    color: "#D32F2F",
    width: "fit-content",
    minWidth: "fit-content",
    textAlign: "left",
    top: "18px",
    borderRadius: "8px",
    fontSize: "14px",
    padding: "12px",
  });

  const getModalConfig = () => {
    const configs = {
      delete: {
        title: "Delete",
        message: hasInProgressItems()
          ? "One or more selected items are currently in progress and cannot be deleted. Please wait for them to complete or cancel them first."
          : "Are you sure you want to delete the selected items?",
        confirmText: isDeleting ? "Deleting..." : "Delete",
        confirmClass: "btn-danger",
        disableConfirm: hasInProgressItems(),
      },
      "delete permanently": {
        title: "Delete Permanently",
        message:
          selectedJobIds.length > 0 && selectedFileIds.length > 0
            ? "Are you sure you want to permanently delete the selected jobs and files? This action cannot be undone."
            : selectedJobIds.length > 0
            ? `Are you sure you want to permanently delete ${
                selectedJobIds.length > 1 ? "these jobs" : "this job"
              }? This action cannot be undone.`
            : `Are you sure you want to permanently delete ${
                selectedFileIds.length > 1 ? "these files" : "this file"
              }? This action cannot be undone.`,
        confirmText: isDeletingpermanently
          ? "Deleting..."
          : "Delete Permanently",
        confirmClass: "btn-danger",
      },
      restore: {
        title: "Restore",
        message: modalState.jobId
          ? `Are you sure you want to restore ${
              modalState.fileId ? "the deleted file" : "this job"
            }? Restoring will reinstate ${
              modalState.fileId ? "the file" : "the job"
            } and make it available for processing again.`
          : `Are you sure you want to restore ${selectedJobIds.length} selected jobs? Restoring will reinstate the jobs and make them available for processing again.`,
        confirmText: "Restore",
        confirmClass: "btn-primary",
      },
    };
    return modalState.action ? configs[modalState.action] : configs.delete;
  };

  const handleCheckboxChange = (id, isFile = false, jobId = null) => {
    const prefix = isFile ? "file-" : "job-";
    const key = `${prefix}${id}`;
    const jobKey = jobId !== null ? `job-${jobId}` : null;
    setCheckedRows((prev) => {
      const newChecked = { ...prev };
      newChecked[key] = !prev[key];
      const currentViewData = isDeletedView
        ? inactiveJobs
        : activeTab === "draftjobs"
        ? draftJobs
        : mergedActiveJobs;
      if (isFile && jobKey) {
        const job = currentViewData.find((j) => `job-${j.job_id}` === jobKey);
        if (job?.files) {
          const fileKeys = job.files.map((f) => `file-${f.file_id}`);
          const allFilesChecked = fileKeys.every((k) => newChecked[k]);
          const someFilesChecked = fileKeys.some((k) => newChecked[k]);
          newChecked[jobKey] = allFilesChecked;
          setIndeterminateRows((prevInd) => ({
            ...prevInd,
            [jobKey]: someFilesChecked && !allFilesChecked,
          }));
        }
      } else {
        const job = currentViewData.find((j) => `job-${j.job_id}` === key);
        job?.files?.forEach((file) => {
          const fileKey = `file-${file.file_id}`;
          newChecked[fileKey] = newChecked[key];
        });
        setIndeterminateRows((prevInd) => ({
          ...prevInd,
          [key]: false,
        }));
      }
      return newChecked;
    });
  };

  useEffect(() => {
    const newSelectedJobIds = [];
    const newSelectedFileIds = [];
    for (const key of Object.keys(checkedRows)) {
      if (checkedRows[key]) {
        const [type, id] = key.split("-");
        const numericId = Number.parseInt(id, 10);
        if (!isNaN(numericId)) {
          if (type === "job") {
            newSelectedJobIds.push(numericId);
          } else {
            newSelectedFileIds.push(numericId);
          }
        }
      }
    }
    setSelectedJobIds(newSelectedJobIds);
    setSelectedFileIds(newSelectedFileIds);
    setIsDeleteEnabled(
      newSelectedJobIds.length + newSelectedFileIds.length > 0
    );
  }, [checkedRows]);

  const toggleSelectAll = () => {
    const currentViewData = isDeletedView
      ? inactiveJobs
      : activeTab === "draftjobs"
      ? draftJobs
      : mergedActiveJobs;
    const newChecked = { ...checkedRows };
    const isSelecting = !selectAll;
    currentViewData.forEach((job) => {
      const jobKey = `job-${job.job_id}`;
      newChecked[jobKey] = isSelecting;
      job.files?.forEach((file) => {
        const fileKey = `file-${file.file_id}`;
        newChecked[fileKey] = isSelecting;
      });
    });
    setCheckedRows(newChecked);
    setSelectAll(isSelecting);
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleLogClick = (jobId, jobName, fileId = null, fileName = null) => {
    setselectedJobname(jobName);
    setselectedFilename(fileName);
    setSelectedJobId(jobId);
    setSelectedFileId(fileId);
    logsetShow(true);
  };

  // *** MODIFICATION START: Pre-check file size before opening modal ***
  const handleConversationClick = async (
    jobId,
    fileId,
    jobName,
    fileName,
    status
  ) => {
    const apiBase = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    const authToken = sessionStorage.getItem("access_token");

    try {
      if (!authToken) {
        throw new Error("Authentication token missing.");
      }

      // Perform a pre-flight check to see if the file content can be fetched
      await axios.get(`${apiBase}/file_content/${jobId}?file_id=${fileId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          Accept: "application/json",
        },
      });

      // If the request succeeds, it means the file is not too large. Proceed to open the modal.
      setselectedJobname(jobName);
      setselectedFilename(fileName);
      setSelectedJobId(jobId);
      setSelectedFileId(fileId);
      setSelectedFileStatus(status);
      consetShow(true);
    } catch (error) {
      // Check specifically for the 413 "Request Entity Too Large" error
      if (error.response && error.response.status === 413) {
        const errorMessage =
          error.response.data?.detail ||
          "File is too large to display. Please download to view.";
        // Display the error in a toast on the current page and DO NOT open the modal
        toast.error(errorMessage, { style: ErrorToastStyle() });
      } else {
        // Handle other potential errors (e.g., 404, 500)
        console.error("Failed to pre-check file content:", error);
        toast.error("Could not open conversion view. Please try again.", {
          style: ErrorToastStyle(),
        });
      }
    }
  };
  // *** MODIFICATION END ***

  const handleReportsClick = (jobId) => {
    console.log("MigrationTable tttttt jobId:", jobId);
    console.log("Navigating with jobId:", jobId);
    console.log("MigrationTable received jobId:", jobId);
    navigate("/migration/viewReportspage", { state: { jobId: jobId } });
  };

  const handleDownload = async (jobId, fileId = null) => {
    const apiBasedownload = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) throw new Error("Authentication token missing.");
      const params = new URLSearchParams();
      params.append("job_id", jobId);
      if (fileId) params.append("file_id", fileId);
      const response = await axios.get(`${apiBasedownload}/download`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params,
        responseType: "blob",
      });
      const contentDisposition = response.headers["content-disposition"];
      const filename =
        contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
        (fileId ? `file-${fileId}.zip` : `job-${jobId}.zip`);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setTimeout(() => {
        toast.success(
          "The file you selected has been downloaded successfully.",
          {
            style: {
              background: "#d4f4d2",
              color: "#000",
              width: "fit-content",
              textAlign: "left",
              top: "47px",
              borderRadius: "8px",
              fontSize: "14px",
              padding: "12px",
            },
            progressStyle: {
              background: "#50b848",
            },
          }
        );
      }, 2000);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed", {
        style: ErrorToastStyle(),
      });
    }
  };

  const handleEditClick = () => {
    if (isEditEnabled) {
      const selectedJobKey = Object.keys(checkedRows).find(
        (key) => key.startsWith("job-") && checkedRows[key]
      );
      if (selectedJobKey) {
        const jobId = selectedJobKey.split("-")[1];
        const selectedJob = data.find((job) => job.job_id == jobId);
        if (selectedJob) {
          onEditJob(selectedJob);
          setShowEdit(true);
        }
      }
    }
  };

  useEffect(() => {
    const connectWebSocket = () => {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        setError("Authentication token missing");
        setLoading(false);
        return;
      }
      const wsBaseMigration =
        process.env.REACT_APP_API_URL_CODE_CONVERSION_WEB_SOCKET;
      socketRef.current = new WebSocket(
        `${wsBaseMigration}/ws/migration_jobs?token=${authToken}`
      );
      socketRef.current.onopen = () => {
        setError(null);
        setLoading(false);
        retryDelayRef.current = 1000;
      };
      socketRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.event === "initial_load" || message.event === "update") {
            const processedActiveJobs = (message.data.active_jobs || []).map(
              (job) => ({
                ...job,
                job_id: Number(job.job_id),
                files: (job.files || []).map((file) => ({
                  ...file,
                  file_id: Number(file.file_id),
                })),
              })
            );
            const processedInactiveJobs = (
              message.data.inactive_jobs || []
            ).map((job) => ({
              ...job,
              job_id: Number(job.job_id),
              files: (job.files || []).map((file) => ({
                ...file,
                file_id: Number(file.file_id),
              })),
            }));
            const processedDraftJobs = (message.data.draft_jobs || []).map(
              (job) => ({
                ...job,
                job_id: Number(job.job_id),
                files: (job.files || []).map((file) => ({
                  ...file,
                  file_id: Number(file.file_id),
                })),
              })
            );
            setActiveJobs((prevActive) =>
              isEqual(prevActive, processedActiveJobs)
                ? prevActive
                : processedActiveJobs
            );
            // The server has now confirmed these jobs - drop the local
            // optimistic placeholders for them so the real data takes over.
            setOptimisticJobs((prevOptimistic) =>
              prevOptimistic.length === 0
                ? prevOptimistic
                : prevOptimistic.filter(
                    (oj) =>
                      !processedActiveJobs.some(
                        (rj) => rj.job_id === oj.job_id
                      )
                  )
            );
            setInactiveJobs((prevInactive) =>
              isEqual(prevInactive, processedInactiveJobs)
                ? prevInactive
                : processedInactiveJobs
            );
            setDraftJobs((prevDraft) =>
              isEqual(prevDraft, processedDraftJobs)
                ? prevDraft
                : processedDraftJobs
            );
          }
        } catch (err) {
          console.error("WebSocket message parsing error:", err);
        }
      };
      socketRef.current.onclose = (event) => {
        if (!event.wasClean) {
          console.warn("WebSocket closed unexpectedly:", event);
        }
        if (!document.hidden) {
          if (retryDelayRef.current >= maxRetryDelay) {
            setError("Connection lost. Retrying...");
          }
          setTimeout(connectWebSocket, retryDelayRef.current);
          retryDelayRef.current = Math.min(
            retryDelayRef.current * 2,
            maxRetryDelay
          );
        }
      };
      socketRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        socketRef.current?.close();
      };
    };
    const handleVisibilityChange = () => {
      if (document.hidden && socketRef.current) {
        socketRef.current.close();
      } else if (
        !document.hidden &&
        (!socketRef.current ||
          socketRef.current.readyState === WebSocket.CLOSED)
      ) {
        connectWebSocket();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    connectWebSocket();
    return () => {
      if (socketRef.current) socketRef.current.close();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshKey]);

  useEffect(() => {
    const jobsToDisplay = isDeletedView
      ? inactiveJobs
      : activeTab === "draftjobs"
      ? draftJobs
      : mergedActiveJobs;
    if (!isEqual(data, jobsToDisplay)) {
      setData(jobsToDisplay);
      setFilteredData(jobsToDisplay);
    }
  }, [isDeletedView, mergedActiveJobs, inactiveJobs, draftJobs, activeTab]);

  useEffect(() => {
    const currentViewData = isDeletedView
      ? inactiveJobs
      : activeTab === "draftjobs"
      ? draftJobs
      : mergedActiveJobs;
    let totalChecked = 0;
    let totalItems = 0;
    currentViewData.forEach((job) => {
      totalItems++;
      const jobKey = `job-${job.job_id}`;
      if (checkedRows[jobKey]) totalChecked++;
      job.files?.forEach((file) => {
        totalItems++;
        const fileKey = `file-${file.file_id}`;
        if (checkedRows[fileKey]) totalChecked++;
      });
    });
    const allChecked = totalChecked === totalItems && totalItems > 0;
    const someChecked = totalChecked > 0 && totalChecked < totalItems;
    setSelectAll(allChecked);
    setHeaderIndeterminate(someChecked);
    setIsDeleteEnabled(totalChecked > 0);
    const selectedJobs = currentViewData.filter(
      (job) => checkedRows[`job-${job.job_id}`]
    );
    setIsEditEnabled(selectedJobs.length === 1);
  }, [
    checkedRows,
    isDeletedView,
    activeTab,
    draftJobs,
    mergedActiveJobs,
    inactiveJobs,
  ]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = headerIndeterminate;
    }
  }, [headerIndeterminate]);

  useEffect(() => {
    const ids = new Set();
    filteredData.forEach((job) => {
      ids.add(`job-${job.job_id}`);
      job.files?.forEach((file) => ids.add(`file-${file.file_id}`));
    });
    setVisibleIds(ids);
  }, [filteredData]);

  useEffect(() => {
    const newIndeterminate = {};
    filteredData.forEach((job) => {
      const jobKey = `job-${job.job_id}`;
      if (job.files) {
        const fileKeys = job.files.map((f) => `file-${f.file_id}`);
        const allFilesChecked = fileKeys.every((k) => checkedRows[k]);
        const someFilesChecked = fileKeys.some((k) => checkedRows[k]);
        newIndeterminate[jobKey] = someFilesChecked && !allFilesChecked;
      }
    });
    setIndeterminateRows(newIndeterminate);
  }, [checkedRows, filteredData]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (data.length > 0 && executingJobs.length > 0) {
      const completedJobs = executingJobs.filter((jobId) => {
        const job = data.find((j) => j.job_id === jobId);
        const shouldProcess = isDeletedView
          ? job?.is_deleted
          : !job?.is_deleted;
        return job && job.status !== "In Progress" && shouldProcess;
      });
      if (completedJobs.length > 0) {
        setExecutingJobs((prev) =>
          prev.filter((id) => !completedJobs.includes(id))
        );
      }
    }
  }, [data, executingJobs, isDeletedView]);

  const renderActions = (row, isFile = false, file = null) => {
    const itemKey = isFile ? `file-${file.file_id}` : `job-${row.job_id}`;
    const isSelected = checkedRows[itemKey] || false;
    const isCoalesceTarget =
      row.target_db && row.target_db.toLowerCase() === "coalesce";
    const itemStatus = isFile ? file.status : row.status;
    const isInProgress = itemStatus === "In Progress";
    const isFailed = itemStatus === "Failed";
    const shouldShowDisabledTooltip = isInProgress || isFailed;

    if (isDeletedView) {
      return (
        <div className="dropdown d-sm-inline-block">
          <i
            className="fas fa-ellipsis-v cursor-pointer"
            data-bs-toggle="dropdown"
          ></i>
          <div className="dropdown-menu dropdown-menu-end min-width-130px">
            <a
              href="#"
              onClick={(e) => {
                if (!isSelected) {
                  e.preventDefault();
                  return;
                }
                e.preventDefault();
                setModalState({
                  isOpen: true,
                  action: "restore",
                  jobId: row.job_id,
                  fileId: isFile ? file.file_id : null,
                  isLoading: false,
                });
              }}
              className={`dropdown-item notify-item ${
                !isSelected ? "disabled" : ""
              }`}
              style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
            >
              <p className="font-size-12 mb-0">
                <i className="fas fa-undo pe-2"></i>Restore
              </p>
            </a>
            <a
              href="#"
              onClick={(e) => {
                if (!isSelected) {
                  e.preventDefault();
                  return;
                }
                e.preventDefault();
                const itemStatus = isFile ? file.status : row.status;
                if (itemStatus === "In Progress") {
                  toast.dismiss();
                  toast.error(
                    `Cannot delete ${
                      isFile ? "file" : "job"
                    } while it's in progress. Please try again later.`,
                    {
                      style: ErrorToastStyle(),
                    }
                  );
                  return;
                }
                const itemsToDelete = {
                  jobIds: isFile ? [] : [row.job_id],
                  fileIds: isFile ? [file.file_id] : [],
                };
                setModalState({
                  isOpen: true,
                  action: "delete permanently",
                  jobId: row.job_id,
                  fileId: isFile ? file.file_id : null,
                  isLoading: false,
                  itemsToDelete,
                });
              }}
              className={`dropdown-item notify-item ${
                !isSelected ? "disabled" : ""
              }`}
              style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
            >
              <p className="font-size-12 mb-0">
                <i className="fas fa-trash pe-2"></i>Delete Permanently
              </p>
            </a>
          </div>
        </div>
      );
    }
    if (activeTab === "draftjobs") {
      return (
        <div className="dropdown d-sm-inline-block">
          <i
            className="fas fa-ellipsis-v cursor-pointer"
            data-bs-toggle="dropdown"
          ></i>
          <div className="dropdown-menu dropdown-menu-end min-width-130px">
            <a
              href="#"
              onClick={(e) => {
                if (!isSelected) {
                  e.preventDefault();
                  return;
                }
                e.preventDefault();
                handleRunDraftJob(row.job_id);
              }}
              className={`dropdown-item notify-item ${
                !isSelected ? "disabled" : ""
              }`}
              style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
            >
              <p className="font-size-12 mb-0">
                <i className="fas fa-play pe-2"></i>Run
              </p>
            </a>
          </div>
        </div>
      );
    }
    return (
      <div className="dropdown d-sm-inline-block">
        <i
          className="fas fa-ellipsis-v cursor-pointer"
          data-bs-toggle="dropdown"
        ></i>
        <div className="dropdown-menu dropdown-menu-end min-width-130px">
          <a
            href="#"
            onClick={() =>
              handleLogClick(
                row.job_id,
                row.job_name,
                isFile ? file.file_id : null,
                isFile ? file.file_name : null
              )
            }
            className={`dropdown-item notify-item ${
              !isSelected ? "disabled" : ""
            }`}
            style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
          >
            <p className="font-size-12 mb-0">
              <i className="fa-solid fa-file-lines pe-2"></i>Logs
            </p>
          </a>
          <div
            style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
          >
            <DownloadWithPopup
              jobId={row.job_id}
              jobName={row.job_name}
              fileId={isFile ? file.file_id : null}
              fileCount={isFile ? 1 : row.file_count}
              onConfirm={handleDownload}
              className={`dropdown-item notify-item ${
                !isSelected ? "disabled" : ""
              }`}
            >
              <p className="font-size-12 mb-0">
                <i className="fa-solid fa-download pe-2"></i>Download
              </p>
            </DownloadWithPopup>
          </div>
          {!isSelected ? (
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="dropdown-item notify-item disabled"
              style={{ opacity: 0.5, pointerEvents: "none" }}
            >
              <p className="font-size-12 mb-0">
                <i className="fab fa-github pe-2"></i>GitHub Upload
              </p>
            </a>
          ) : !isGithubConfigured ? (
            <CustomTooltip
              content="Please configure GitHub Integration in Settings to use this feature."
              placement="left"
            >
              <div
                className="dropdown-item notify-item disabled"
                style={{ opacity: 0.5, pointerEvents: "none" }}
              >
                <p className="font-size-12 mb-0">
                  <i className="fab fa-github pe-2"></i>GitHub Upload
                </p>
              </div>
            </CustomTooltip>
          ) : isCoalesceTarget && !isFile ? (
            <CustomTooltip
              content="GitHub upload is only available at file level for Coalesce targets."
              placement="left"
            >
              <div
                className="dropdown-item notify-item disabled"
                style={{ opacity: 0.5, pointerEvents: "none" }}
              >
                <p className="font-size-12 mb-0">
                  <i className="fab fa-github pe-2"></i>GitHub Upload
                </p>
              </div>
            </CustomTooltip>
          ) : shouldShowDisabledTooltip ? (
            <CustomTooltip
              content={
                isInProgress
                  ? `This ${
                      isFile ? "file" : "job"
                    } is in progress. Please wait until it's completed to upload to GitHub.`
                  : `This ${
                      isFile ? "file" : "job"
                    } has failed and cannot be uploaded to GitHub.`
              }
              placement="left"
            >
              <div
                className="dropdown-item notify-item disabled"
                style={{ opacity: 0.5, pointerEvents: "none" }}
              >
                <p className="font-size-12 mb-0">
                  <i className="fab fa-github pe-2"></i>GitHub Upload
                </p>
              </div>
            </CustomTooltip>
          ) : (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleGitHubUploadClick(
                  row.job_id,
                  row.job_name,
                  isFile ? file.file_id : null,
                  isFile ? file.file_name : null,
                  row.source_db,
                  row.target_db
                );
              }}
              className="dropdown-item notify-item"
            >
              <p className="font-size-12 mb-0">
                <i className="fab fa-github pe-2"></i>GitHub Upload
              </p>
            </a>
          )}
          {!isFile && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleReportsClick(row.job_id);
              }}
              className={`dropdown-item notify-item ${
                !isSelected ? "disabled" : ""
              }`}
              style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
            >
              <p className="font-size-12 mb-0">
                <i className="fa-solid fa-chart-column pe-2"></i> Reports
              </p>
            </a>
          )}
          {isFile && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleConversationClick(
                  row.job_id,
                  file.file_id,
                  row.job_name,
                  file.file_name,
                  file.status
                );
              }}
              className={`dropdown-item notify-item ${
                !isSelected ? "disabled" : ""
              }`}
              style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}
            >
              <p className="font-size-12 mb-0">
                <i className="fas fa-comments pe-2"></i>View Conversion
              </p>
            </a>
          )}
        </div>
      </div>
    );
  };

  const modalConfig = getModalConfig();

  if (loading) {
    return (
      <div className="migration-table-container">
        <div className="d-flex justify-content-between mb-3">
          <div
            className="loadingStyle"
            style={{ width: "300px", height: "40px" }}
          ></div>
          <div
            className="loadingStyle"
            style={{ width: "200px", height: "40px" }}
          ></div>
        </div>
        <div className="d-flex mb-3">
          <div
            className="loadingStyle"
            style={{ width: "140px", height: "30px", marginRight: "10px" }}
          ></div>
          <div
            className="loadingStyle"
            style={{ width: "140px", height: "30px" }}
          ></div>
        </div>
        <table className="table">
          <thead>
            <tr>
              {[...Array(8)].map((_, i) => (
                <th key={i}>
                  <div
                    className="loadingStyle"
                    style={{ width: "80%", height: "20px" }}
                  ></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, rowIndex) => (
              <tr key={rowIndex}>
                {[...Array(8)].map((_, cellIndex) => (
                  <td key={cellIndex}>
                    <div
                      className="loadingStyleloadingStyle"
                      style={{
                        width:
                          cellIndex === 0
                            ? "20px"
                            : `${80 + Math.random() * 20}%`,
                        height: "16px",
                        margin: cellIndex === 0 ? "0 auto" : "0",
                      }}
                    ></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return <div className="text-center mt-3 text-danger">Error: {error}</div>;
  }

  return (
    <div
      className={`migration-table-container ${
        isDeletedView ? "deleted-view-active" : ""
      }`}
    >
      <MigrationBreadcrumb
        isDeletedView={isDeletedView}
        setShow={setShow}
        setCurrentStep={setCurrentStep}
        onDelete={handleDelete}
        isDeleteEnabled={isDeleteEnabled}
        isDeleting={isDeleting}
        onEditClick={handleEditClick}
        isEditEnabled={isEditEnabled}
        onBulkRestore={handleBulkRestore}
        hasSelectedItems={selectedJobIds.length + selectedFileIds.length > 0}
        hasSelectedJobs={selectedJobIds.length > 0}
        hasSelectedFiles={selectedFileIds.length > 0}
        onCancelSelection={handleCancelSelection}
        showCreateButton={!isDeletedView && activeTab === "migrationjobs"}
        isPermanentDeleteEnabled={
          selectedJobIds.length + selectedFileIds.length > 0
        }
        onPermanentDelete={handlePermanentDelete}
        isDeletingPermanently={isDeletingpermanently}
        hasInProgressItems={hasInProgressItems}
      />
      <div className="search-bar my-2 position-relative">
        <span
          className="ri-search-line position-absolute"
          style={{
            top: "50%",
            left: "10px",
            transform: "translateY(-50%)",
            fontSize: "16px",
          }}
        ></span>
        <input
          type="text"
          placeholder="Search"
          className="form-control highlighted-input font-size-12 doc-search text-d-search pr-5 colorfor-placeholder styled-input"
          style={{ paddingLeft: "30px" }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      {!isDeletedView && (
        <ul
          className="nav nav-tabs nav-tabs-custom nav-justified w-fit-content mb-1"
          role="tablist"
        >
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link tab-col w-fit-content ${
                activeTab === "migrationjobs" ? "active" : ""
              }`}
              onClick={() => navigate("/migration")}
              style={{
                backgroundColor:
                  activeTab === "migrationjobs" ? "#dcdfe3" : "transparent",
                borderRadius: "8px 8px 0 0",
                padding: "10px 20px",
                width: "140px",
                height: "29px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "21px",
                letterSpacing: "0%",
                position: "relative",
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span className="d-sm-block white-space-nowrap">
                Code Conversions
                {activeTab === "migrationjobs" && (
                  <span
                    style={{
                      display: "block",
                      height: "1px",
                      backgroundColor: "#354258",
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  />
                )}
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link tab-col w-fit-content ${
                activeTab === "draftjobs" ? "active" : ""
              }`}
              onClick={() => navigate("/migration/draft_jobs")}
              style={{
                backgroundColor:
                  activeTab === "draftjobs" ? "#dcdfe3" : "transparent",
                borderRadius: "8px 8px 0 0",
                padding: "10px 20px",
                width: "140px",
                height: "29px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "21px",
                letterSpacing: "0%",
                position: "relative",
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span className="d-sm-block white-space-nowrap">
                Drafts
                {activeTab === "draftjobs" && (
                  <span
                    style={{
                      display: "block",
                      height: "1px",
                      backgroundColor: "#354258",
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  />
                )}
              </span>
            </button>
          </li>
        </ul>
      )}
      {isDeletedView && (
        <ul
          className="nav nav-tabs nav-tabs-custom nav-justified w-fit-content mb-1"
          role="tablist"
        >
          <li className="nav-item" role="presentation">
            <a
              className="nav-link tab-col active w-fit-content"
              data-bs-toggle="tab"
              href="#migrationjobs"
              role="tab"
              aria-selected="true"
              style={{
                backgroundColor: "#dcdfe3",
                borderRadius: "8px 8px 0 0",
                padding: "10px 20px",
                width: "140px",
                height: "29px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "21px",
                letterSpacing: "0%",
                position: "relative",
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <span className="d-sm-block white-space-nowrap">
                Deleted Code Conversions
                <span
                  style={{
                    display: "block",
                    height: "1px",
                    backgroundColor: "#354258",
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                />
              </span>
            </a>
          </li>
        </ul>
      )}
      <div className="tab-content tab-con">
        <div
          className="tab-pane active"
          id="migrationjobs"
          role="tabpanel"
          style={{
            maxHeight: "67vh",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <table className="table">
            <thead className="table-head fw-bold">
              <tr>
                <th style={{ ...cellStyle, fontSize: "16px" }}>
                  <input
                    type="checkbox"
                    className="custom-checkbox mb-0"
                    checked={selectAll}
                    style={{ width: "17px" }}
                    ref={headerCheckboxRef}
                    onChange={toggleSelectAll}
                    disabled={filteredData.length === 0}
                  />
                </th>
                <th style={{ ...cellStyle, width: "18%", fontSize: "11px" }}>
                  Job
                </th>
                <th style={{ ...cellStyle, width: "25%", fontSize: "11px" }}>
                  Description
                </th>
                <th style={{ ...cellStyle, width: "10%", fontSize: "11px" }}>
                  Source
                </th>
                <th style={{ ...cellStyle, width: "10%", fontSize: "11px" }}>
                  Target
                </th>
                <th style={{ ...cellStyle, width: "15%", fontSize: "11px" }}>
                  Status
                </th>
                <th style={{ ...cellStyle, width: "15%", fontSize: "11px" }}>
                  Started{" "}
                </th>
                {activeTab !== "draftjobs" && (
                  <th
                    style={{
                      ...cellStyle,
                      width: "18%",
                      fontSize: "11px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Completed
                  </th>
                )}
                <th style={{ ...cellStyle, width: "5%", fontSize: "11px" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="scrollable-tbody" style={{ zIndex: 1 }}>
              {filteredData.length > 0 ? (
                filteredData.map((row) => (
                  <React.Fragment key={row.job_id}>
                    <tr
                      style={{
                        ...cellStyle,
                        backgroundColor: checkedRows[`job-${row.job_id}`]
                          ? "#F1F3FB"
                          : "transparent",
                      }}
                    >
                      <td className="pt-2" style={cellStyle}>
                        <div className="d-flex align-items-center">
                          <input
                            key={`checkbox-${row.job_id}`}
                            type="checkbox"
                            checked={checkedRows[`job-${row.job_id}`] || false}
                            className="custom-checkbox mr-2"
                            ref={(el) => {
                              if (el)
                                el.indeterminate =
                                  indeterminateRows[`job-${row.job_id}`] ||
                                  false;
                            }}
                            onChange={() => handleCheckboxChange(row.job_id)}
                          />
                          <div className="ms-2 cursor-pointer">
                            {row.files?.length > 0 &&
                              activeTab !== "draftjobs" && (
                                <i
                                  className={`fas ${
                                    expandedRows[row.job_id]
                                      ? "fa-chevron-down"
                                      : "fa-chevron-right"
                                  } cursor-pointer me-2`}
                                  onClick={() => toggleRow(row.job_id)}
                                />
                              )}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...cellStyle, width: "18%" }}>
                        <TruncatedText
                          text={
                            row.job_name
                              ? row.job_name.charAt(0).toUpperCase() +
                                row.job_name.slice(1)
                              : null
                          }
                          maxLength={12}
                          jobId={row.job_id}
                          newlyCreatedJob={newlyCreatedJob}
                        />
                      </td>
                      <td style={{ ...cellStyle, width: "25%" }}>
                        <TruncatedText
                          text={
                            row.description
                              ? row.description.charAt(0).toUpperCase() +
                                row.description.slice(1)
                              : null
                          }
                        />
                      </td>
                      <td
                        style={{
                          ...cellStyle,
                          width: "10%",
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        <TruncatedText
                          text={row.source_db || "N/A"}
                          maxLength={14}
                        />
                      </td>
                      <td
                        style={{
                          ...cellStyle,
                          width: "10%",
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        <TruncatedText
                          text={row.target_db || "N/A"}
                          maxLength={14}
                        />
                      </td>
                      <td style={cellStyle}>
                        <div
                          className={`status-container ${
                            row.status === "In Progress"
                              ? "executing"
                              : row.status
                              ? row.status.toLowerCase().replace(" ", "-")
                              : "default"
                          }`}
                        >
                          {row.status || "N/A"}
                        </div>
                      </td>
                      <td
                        style={{
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <i
                            className="ri-calendar-2-line"
                            style={{ fontSize: "14px", marginRight: "4px" }}
                          ></i>
                          <span>{row.created_at || "N/A"}</span>
                        </div>
                      </td>
                      {activeTab !== "draftjobs" && (
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <i
                              className="ri-calendar-2-line"
                              style={{ fontSize: "14px", marginRight: "4px" }}
                            ></i>
                            <span>{row.completed_at || "N/A"}</span>
                          </div>
                        </td>
                      )}
                      <td style={{ ...cellStyle, textAlign: "center" }}>
                        {renderActions(row)}
                      </td>
                    </tr>
                    {activeTab !== "draftjobs" &&
                      expandedRows[row.job_id] &&
                      (row.files?.length > 0 ? (
                        row.files.map((file) => (
                          <tr
                            key={file.file_id}
                            style={{
                              ...cellStyle,
                              backgroundColor: checkedRows[
                                `file-${file.file_id}`
                              ]
                                ? "#F1F3FB"
                                : "transparent",
                            }}
                            className="child-row"
                          >
                            <td
                              style={{
                                ...cellStyle,
                                width: "18%",
                                maxWidth: "250px",
                              }}
                            >
                              <div
                                className="d-flex align-items-center ps-1"
                                style={{ gap: "8px" }}
                              >
                                <input
                                  key={`checkbox-file-${file.file_id}`}
                                  type="checkbox"
                                  checked={
                                    checkedRows[`file-${file.file_id}`] || false
                                  }
                                  className="custom-checkbox"
                                  onChange={() =>
                                    handleCheckboxChange(
                                      file.file_id,
                                      true,
                                      row.job_id
                                    )
                                  }
                                  style={{
                                    flexShrink: 0,
                                    alignSelf: "center",
                                  }}
                                />
                                <span
                                  style={{
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                    maxWidth: "250px",
                                  }}
                                >
                                  {file.file_name || "N/A"}
                                </span>
                              </div>
                            </td>
                            <td style={{ ...cellStyle, width: "25%" }}>
                              {file.raw_file_path || "N/A"}
                            </td>
                            <td style={{ ...cellStyle, width: "10%" }}>
                              <TruncatedText
                                text={row.source_db || "N/A"}
                                maxLength={14}
                              />
                            </td>
                            <td style={{ ...cellStyle, width: "10%" }}>
                              <TruncatedText
                                text={row.target_db || "N/A"}
                                maxLength={14}
                              />
                            </td>
                            <td style={cellStyle}>
                              <div
                                className={`status-container ${
                                  file.status === "In Progress"
                                    ? "executing"
                                    : file.status
                                    ? file.status
                                        .toLowerCase()
                                        .replace(" ", "-")
                                    : "default"
                                }`}
                              >
                                {file.status || "N/A"}
                              </div>
                            </td>
                            <td
                              style={{
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <i
                                  className="ri-calendar-2-line"
                                  style={{
                                    fontSize: "14px",
                                    marginRight: "4px",
                                  }}
                                ></i>
                                <span> {file.file_created_at || "N/A"}</span>
                              </div>
                            </td>
                            {activeTab !== "draftjobs" && (
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  verticalAlign: "middle",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <i
                                    className="ri-calendar-2-line"
                                    style={{
                                      fontSize: "14px",
                                      marginRight: "4px",
                                    }}
                                  ></i>
                                  <span>
                                    {" "}
                                    {file.file_completed_at || "N/A"}
                                  </span>
                                </div>
                              </td>
                            )}
                            <td style={{ textAlign: "center" }}>
                              {renderActions(row, true, file)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="child-row">
                          <td colSpan="8" className="text-center">
                            No files available
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={activeTab === "draftjobs" ? 8 : 9}
                    className="text-center"
                    style={{
                      height: "calc(clamp(58vh, 62vh + 2vw, 80vh) - 40px)",
                      verticalAlign: "middle",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        padding: "20px 0",
                      }}
                    >
                      <img
                        src={norecords || "/placeholder.svg"}
                        alt="No Records"
                        style={{
                          width: "220px",
                          marginBottom: "20px",
                        }}
                      />
                      <h4 className="fw-bold" style={{ fontSize: "18px" }}>
                        {isDeletedView
                          ? "No Deleted Jobs"
                          : activeTab === "draftjobs"
                          ? "No Draft Jobs"
                          : "No Migration Jobs"}
                      </h4>
                      <p>
                        {!isDeletedView
                          ? activeTab === "draftjobs"
                            ? "It seems you don't have any draft jobs yet."
                            : "Looks like there are no migration jobs yet. Ready to get started?"
                          : "It looks like you haven't deleted any jobs yet."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {modalState.isOpen && (
        <DeleteConfirmationModal
          isOpen={modalState.isOpen}
          onClose={() => setModalState({ ...modalState, isOpen: false })}
          onConfirm={handleActionConfirm}
          isLoading={modalState.isLoading}
          message={getModalConfig().message}
          confirmText={getModalConfig().confirmText}
          confirmClass={getModalConfig().confirmClass}
          actionType={modalState.action}
        />
      )}
      {githubPanelState.isOpen && (
        <GitHubUploadPanel
          show={githubPanelState.isOpen}
          setShow={(show) =>
            setGithubPanelState((prev) => ({ ...prev, isOpen: show }))
          }
          jobId={githubPanelState.jobId}
          fileId={githubPanelState.fileId}
          jobName={githubPanelState.jobName}
          fileName={githubPanelState.fileName}
          onUploadSuccess={handleGitHubUploadSuccess}
          source_db={githubPanelState.source_db}
          targetDb={githubPanelState.targetDb}
        />
      )}
    </div>
  );
}

export default MigrationTable;
